import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/Layout';
import { API } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, ScrollText, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

const roleColors = {
  ADMIN: 'bg-purple-100 text-purple-700',
  ADMINISTRATIVO: 'bg-blue-100 text-blue-700',
  ENFERMERA: 'bg-emerald-100 text-emerald-700',
  CAJA: 'bg-amber-100 text-amber-700',
};

const formatTs = (ts) => {
  try {
    return new Date(ts).toLocaleString('es-CL', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return ts;
  }
};

export default function ActividadPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchActividad = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/actividad?page=${page}&limit=50`, { withCredentials: true });
      setItems(res.data.items);
      setPages(res.data.pages);
      setTotal(res.data.total);
    } catch (error) {
      toast.error('Error al cargar el registro de actividad');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchActividad(); }, [fetchActividad]);

  return (
    <Layout>
      <div className="space-y-6" data-testid="actividad-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ScrollText className="w-6 h-6 text-blue-600" />
            Registro de Actividad
          </h1>
          <p className="text-slate-500">
            Todos los movimientos del sistema · Se conservan por 30 días
          </p>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <CardTitle className="text-lg">Movimientos ({total})</CardTitle>
                <CardDescription>Ordenados del más reciente al más antiguo</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline" size="sm"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage(p => p - 1)}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-slate-500" data-testid="page-indicator">
                  Página {page} de {pages}
                </span>
                <Button
                  variant="outline" size="sm"
                  disabled={page >= pages || loading}
                  onClick={() => setPage(p => p + 1)}
                  data-testid="next-page-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500" />
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-12 text-slate-500" data-testid="actividad-empty">
                <ScrollText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Aún no hay movimientos registrados</p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-x-auto">
                <Table data-testid="actividad-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha y Hora</TableHead>
                      <TableHead>Usuario</TableHead>
                      <TableHead>Rol</TableHead>
                      <TableHead>Acción</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map(item => (
                      <TableRow key={item.id}>
                        <TableCell className="whitespace-nowrap font-mono text-sm">
                          {formatTs(item.timestamp)}
                        </TableCell>
                        <TableCell className="font-medium">{item.user_name}</TableCell>
                        <TableCell>
                          {item.user_role ? (
                            <Badge className={`${roleColors[item.user_role] || 'bg-slate-100 text-slate-600'} hover:bg-inherit`}>
                              {item.user_role}
                            </Badge>
                          ) : (
                            <span className="text-slate-400 text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell>{item.accion}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
